import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { Button, Card, Col, Row, Space, Tag, Typography, message, Divider } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import BannerUpload from "../components/Campaigns/BannerUpload";
import BannerList from "../components/Campaigns/BannerList";
import PDFPreviewDownload from "../components/Campaigns/PDFPreviewDownload";
import SpinnerFullPage from "../components/SpinnerFullPage";
import ErrorPane from "../components/ErrorPane";
import { coreApi } from "../api/calls";

const { Title, Text } = Typography;

const BannerManagement = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCampaign();
  }, [id]);

  const loadCampaign = async () => {
    setIsLoading(true);
    try {
      const response = await coreApi.get(`/campaigns/${id}`);
      setCampaign(response.data);
    } catch (error) {
      console.error(error);
      setError(error.response?.status || 500);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBannerUploaded = () => {
    loadCampaign();
  };

  const handleBannerDeleted = () => {
    loadCampaign();
  };

  const handleBannersReordered = () => {
    loadCampaign();
  };

  if (isLoading) return <SpinnerFullPage />;
  if (error) return <ErrorPane errorCode={error} />;
  if (!campaign) return <ErrorPane errorCode={404} />;

  return (
    <Row gutter={[16, 24]}>
      <Col span={24}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => history.push("/campaigns")}
                >
                  {t("campaigns.backToCampaigns")}
                </Button>
                <div>
                  <Title level={3} style={{ margin: 0 }}>
                    {campaign.name}
                  </Title>
                  {campaign.description && (
                    <Text type="secondary">{campaign.description}</Text>
                  )}
                </div>
              </Space>
              <Space>
                <Tag color={campaign.is_active ? "green" : "default"}>
                  {campaign.is_active ? t("campaigns.active") : t("campaigns.inactive")}
                </Tag>
                <Tag color={campaign.is_recurring ? "purple" : "orange"}>
                  {campaign.is_recurring ? t("campaigns.recurring") : t("campaigns.oneTime")}
                </Tag>
              </Space>
            </div>

            <Divider />

            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div>
                <Title level={5}>{t("campaigns.pdfPreviewSection")}</Title>
                <Text type="secondary">{t("campaigns.pdfPreviewDescription")}</Text>
              </div>
              <PDFPreviewDownload campaign={campaign} />
            </Space>

            <Divider />

            <BannerUpload
              campaignId={campaign.id}
              onUploadSuccess={handleBannerUploaded}
            />

            <BannerList
              campaignId={campaign.id}
              banners={campaign.banners || []}
              onBannerDeleted={handleBannerDeleted}
              onBannersReordered={handleBannersReordered}
            />
          </Space>
        </Card>
      </Col>
    </Row>
  );
};

export default BannerManagement;
